import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Lock, Loader2 } from "lucide-react";

const GATE_KEY = "tradeiq_gate_token";
// localStorage so the unlock persists across tabs — users can open pages in
// new tabs without re-entering the password. Still one password per device.
const store = typeof window !== "undefined" ? window.localStorage : null;

export default function SiteGate({ children }) {
  const [state, setState] = useState("checking"); // checking | locked | open
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const token = store?.getItem(GATE_KEY) || "";
        const { data } = await api.get("/gate/status", { params: { token } });
        if (!alive) return;
        setState(!data.enabled || data.unlocked ? "open" : "locked");
      } catch {
        // Fail-open so a backend hiccup never bricks the whole site.
        if (alive) setState("open");
      }
    })();
    return () => { alive = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/gate/verify", { password });
      store?.setItem(GATE_KEY, data.token);
      setState("open");
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || "Incorrect password.");
    } finally {
      setSubmitting(false);
    }
  };

  if (state === "checking") {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center" data-testid="site-gate-loading">
        <Loader2 className="h-6 w-6 text-zinc-600 animate-spin" />
      </div>
    );
  }

  if (state === "locked") {
    return (
      <div
        className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-6"
        data-testid="site-gate"
      >
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-8">
            <img src="/brand/ptiq-icon.png" alt="ProTradeIQ" className="h-7 w-7 object-contain" />
            <span className="font-heading text-2xl font-black tracking-tight uppercase">
              ProTrade<span className="text-[#007AFF]">IQ</span>
            </span>
            <span className="px-1.5 py-0.5 border border-[#F59E0B]/40 text-[#F59E0B] text-[9px] uppercase tracking-widest font-mono">
              Private
            </span>
          </div>

          <div className="border border-zinc-800 bg-[#121212] p-6">
            <div className="inline-flex items-center justify-center w-10 h-10 border border-zinc-800 mb-4">
              <Lock className="h-4 w-4 text-[#007AFF]" strokeWidth={1.8} />
            </div>
            <h1 className="font-heading text-xl font-bold mb-1">Access restricted</h1>
            <p className="text-sm text-zinc-500 mb-6">
              This site is private. Enter the access password to continue.
            </p>

            <form onSubmit={submit} className="space-y-3">
              <input
                type="password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Access password"
                className="w-full bg-[#0a0a0a] border border-zinc-800 focus:border-[#007AFF] outline-none px-3 py-2.5 text-sm font-mono placeholder:text-zinc-600"
                data-testid="site-gate-input"
              />
              {error && (
                <p className="text-[11px] text-[#EF4444] font-mono" data-testid="site-gate-error">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={submitting || !password}
                className="w-full bg-[#007AFF] hover:bg-[#0066d6] disabled:opacity-50 text-white text-xs uppercase tracking-widest font-mono py-2.5 inline-flex items-center justify-center gap-2 transition-colors"
                data-testid="site-gate-submit"
              >
                {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Enter
              </button>
            </form>
          </div>

          <p className="text-[10px] text-zinc-700 font-mono uppercase tracking-widest mt-4 text-center">
            Invite-only · access by permission
          </p>
        </div>
      </div>
    );
  }

  return children;
}
